import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { queryClient } from 'src/core/react-query';
import { BackgroundImage } from 'src/core/types';

import axios from './axios';

export interface UseDeleteBackgroundImageVariables {
  id: number;
}

export interface UseDeleteBackgroundImageData {}

export function useDeleteBackgroundImage() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<
    UseDeleteBackgroundImageData,
    AxiosError<UseDeleteBackgroundImageData>,
    UseDeleteBackgroundImageVariables,
    { previousBackgroundImages?: Array<BackgroundImage> }
  >(
    (variables) => {
      const { id } = variables;

      return axios
        .delete<UseDeleteBackgroundImageData>(
          `/posht/picasso/background-images/${id}/`,
        )
        .then((res) => res.data);
    },
    {
      onError: (error, variables, context) => {
        if (context?.previousBackgroundImages) {
          queryClient.setQueryData<Array<BackgroundImage>>(
            'todos',
            context.previousBackgroundImages,
          );
        }

        enqueueSnackbar(t('errors.unknown'), { variant: 'error' });
      },
      onMutate: async ({ id }) => {
        await queryClient.cancelQueries('background-images');

        const previousBackgroundImages =
          queryClient.getQueryData<Array<BackgroundImage>>('background-images');

        if (previousBackgroundImages) {
          const newBackgroundImages = previousBackgroundImages.filter(
            (backgroundImage) => backgroundImage.id !== id,
          );

          queryClient.setQueryData<Array<BackgroundImage>>(
            'background-images',
            newBackgroundImages,
          );
        }

        return { previousBackgroundImages };
      },
      onSettled: () => {
        queryClient.invalidateQueries('background-images');
      },
      onSuccess: () => {
        enqueueSnackbar(t('operation.successful'), { variant: 'success' });
      },
    },
  );
}
